import { useState, useRef } from 'react';
import { guestService, eventService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import SearchableSelect from 'components/SearchableSelect';
import styles from './ImportModal.module.css';

// ── Modal importar Excel ───────────────────────────────────────────────────────
export default function ImportModal({ companies, onClose, onImported }) {
  const [compId,  setCompId]  = useState('');
  const [events,  setEvents]  = useState([]);
  const [eventId, setEventId] = useState('');
  const [file,    setFile]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [result,  setResult]  = useState(null);
  const fileRef = useRef();

  // Plantilla de Excel (hojas «Invitados» e «Instrucciones»), igual que la de propietarios.
  const descargarPlantilla = async () => {
    setError('');
    try {
      const { data } = await guestService.template();
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_invitados.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Con responseType 'blob' el cuerpo del error también llega como Blob: se lee para mostrar el mensaje real.
      let msg = '';
      try { msg = JSON.parse(await err.response?.data?.text?.())?.message || ''; } catch { /* sin cuerpo JSON */ }
      setError(msg || 'No se pudo descargar la plantilla');
    }
  };

  const loadEvents = (cid) => {
    setCompId(cid); setEventId(''); setEvents([]);
    if (!cid) return;
    eventService.getAll({ company_id: cid })
      .then(r => setEvents(r.data?.data || r.data || [])).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!eventId) return setError('Selecciona un evento');
    if (!file)    return setError('Selecciona un archivo Excel');
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file',       file);
      fd.append('event_id',   eventId);
      fd.append('company_id', compId);
      const { data } = await guestService.import(fd);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al importar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalImportar}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><i className={`fas fa-file-excel ${styles.iconoExcel}`} />Importar invitados desde Excel</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        {result ? (
          <div className="modal-body">
            {/* Con filas omitidas o con error NO se muestra un check verde «todo bien»: se avisa y se listan las primeras filas con problema. */}
            {(() => {
              const conProblemas = result.skipped > 0 || (result.errors || []).length > 0;
              return (
                <div className={styles.resultado}>
                  <i className={`${conProblemas ? 'fas fa-exclamation-triangle' : 'fas fa-check-circle'} ${conProblemas ? styles.resultadoIconoAviso : styles.resultadoIconoOk}`} />
                  <div className={styles.resultadoTitulo}>{result.created} invitados importados</div>
                  {result.skipped > 0 && <div className={styles.omitidas}>{result.skipped} filas omitidas o con error</div>}
                </div>
              );
            })()}
            {(result.errors || []).length > 0 && (
              <div className={styles.problemas}>
                <strong>Filas con problema{result.errors.length >= 5 ? ' (se muestran las primeras 5)' : ''}:</strong>
                <ul className={styles.problemasLista}>
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
                Corrige esas filas en el Excel y vuelve a importarlo: los invitados que ya se importaron no se duplican.
              </div>
            )}
            <div className={styles.infoColumnas}>
              <strong>Columnas esperadas en el Excel:</strong><br/>
              nombre, tipo_documento, numero, email, telefono, empresa, cargo
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className={`modal-body ${styles.cuerpo}`}>
              {error && <AvisoModal>{error}</AvisoModal>}
              <div className="form-group">
                <label className="form-label">Compañía *</label>
                <SearchableSelect value={compId} onChange={(id) => loadEvents(id)} placeholder="— Selecciona compañía —" allowEmpty={false} minWidth={0} maxWidth={9999} ariaLabel="Compañía"
                  options={companies.map(c => ({ value: c.id, label: c.name }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Evento *</label>
                <SearchableSelect value={eventId} onChange={(id) => setEventId(id)} disabled={!compId || !events.length} allowEmpty={false} minWidth={0} maxWidth={9999} ariaLabel="Evento"
                  placeholder={!compId ? '— Primero selecciona compañía —' : events.length === 0 ? 'Sin eventos disponibles' : '— Selecciona evento —'}
                  options={events.map(ev => ({ value: ev.id, label: ev.name }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Archivo Excel *</label>
                <div className={file ? styles.zonaConArchivo : styles.zona}
                  onClick={() => fileRef.current?.click()}>
                  {file ? (
                    <div className={styles.archivoFila}>
                      <i className={`fas fa-file-excel ${styles.archivoIcono}`} />
                      <span className={styles.archivoNombre}>{file.name}</span>
                      <button type="button" onClick={ev => { ev.stopPropagation(); setFile(null); }}
                        className={styles.archivoQuitar}>✕</button>
                    </div>
                  ) : (
                    <>
                      <i className={`fas fa-upload ${styles.zonaIcono}`} />
                      <div className={styles.zonaTexto}>Haz clic para seleccionar el archivo .xlsx</div>
                    </>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className={styles.oculto}
                  onChange={e => setFile(e.target.files[0])} />
              </div>
              <div className={styles.infoColumnasChica}>
                <strong>Columnas esperadas:</strong> nombre, tipo_documento, numero, email, telefono, empresa, cargo
                <div className={styles.botonPlantilla}>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={descargarPlantilla}>
                    <i className="fas fa-download" /> Descargar plantilla
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> Importando...</> : <><i className="fas fa-upload" /> Importar</>}
              </button>
            </div>
          </form>
        )}
        {result && (
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={() => { onImported(); onClose(); }}>
              <i className="fas fa-check" /> Listo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
